import { css } from '@emotion/react';
import {
  useLayoutEffect, useRef,
} from 'react';
import { AddMorePlaceholderComponent } from '~/_shared/components/addMorePlaceholder/addMorePlaceholder.component';
import type { Uuid } from '~/_shared/utils/createUuid';
import {
  CHART_ITEM_HEIGHT, ChartItemComponent,
} from './chartItem/chartItem.component';
import type { ChartItem } from './chartItem/chartItem.types';

const addNewStyle = css({
  height: CHART_ITEM_HEIGHT,
});

const addNewIconStyle = css({
  fontSize: 40,
});

const containerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
});

export type ChartsGalleryComponentProps = Readonly<{
  className?: string;
  chartItems: ReadonlyArray<ChartItem>;
  scrollToChartItemId?: Uuid;
  onAddNewChart?: () => void;
  onScrolledToChartItem?: () => void;
}>;

export const ChartsGalleryComponent = ({
  className,
  chartItems,
  scrollToChartItemId,
  onAddNewChart,
  onScrolledToChartItem,
}: ChartsGalleryComponentProps) => {
  const onScrolledToChartItemRef = useRef(onScrolledToChartItem);
  const chartContainersRefs = useRef<Record<Uuid, HTMLDivElement | null>>({});

  // Effect to handle scrolling when scrollToChartItemId changes
  useLayoutEffect(() => {
    if (scrollToChartItemId) {
      const chartElement = chartContainersRefs.current[scrollToChartItemId];

      if (chartElement) {
        onScrolledToChartItemRef.current?.();

        setTimeout(() => {
          const chartElementRefAfterTimeout = chartContainersRefs.current[scrollToChartItemId];
          // Scroll the element into view with smooth behavior
          chartElementRefAfterTimeout?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 1000);
      }
    }
  }, [scrollToChartItemId, onScrolledToChartItemRef]);

  return (
    <div className={className} css={containerStyle}>
      {onAddNewChart && (
        <AddMorePlaceholderComponent
          css={addNewStyle}
          width={90}
          onClick={onAddNewChart}
          iconStyle={addNewIconStyle}
        />
      )}
      {chartItems.map((chartItem) => (
        <ChartItemComponent
          ref={el => {
            chartContainersRefs.current[chartItem.id] = el;
          }}
          key={chartItem.id}
          {...chartItem}
        />
      ))}
    </div>
  );
};
